import React, { useState, useEffect, useLayoutEffect, useRef } from "react"
import ReactDOM from "react-dom"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap"
import { BASE_URL } from "../../../global"
import { withTranslation } from "react-i18next"
import axios from "axios"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import jwt_decode from "jwt-decode"
import { getTutorAuthToken } from "../../../common/auth/tutor-admin-auth"

/** Breathing room between the manuals menu and the button or screen edge. */
const MANUAL_MENU_GAP = 4

const NotificationDropdown = props => {
  const { variant = "header", compact = false } = props

  const [reportCount, setReportCount] = useState(0)
  const [tutorNotifCount, setTutorNotifCount] = useState(0)
  const [manualMenu, setManualMenu] = useState(false)
  const manualBtnRef = useRef(null)
  const manualMenuRef = useRef(null)
  const [manualMenuPos, setManualMenuPos] = useState({ top: 0, left: 0 })

  // The button sits in the sidebar footer, pinned to the bottom of the
  // viewport, so a menu opening downward runs off the screen and every manual
  // added hides one more. Measure the menu that just rendered and put it above
  // the button unless there is genuinely room below.
  //
  // useLayoutEffect, not useEffect: this runs after the portal is in the DOM
  // but before paint, so the menu is never seen at the wrong position.
  useLayoutEffect(() => {
    if (!manualMenu || !manualBtnRef.current) return
    const rect = manualBtnRef.current.getBoundingClientRect()
    const menuHeight = manualMenuRef.current
      ? manualMenuRef.current.offsetHeight
      : 0
    const below = rect.bottom + MANUAL_MENU_GAP
    const fitsBelow = below + menuHeight <= window.innerHeight - MANUAL_MENU_GAP
    setManualMenuPos({
      top: fitsBelow
        ? below
        : Math.max(MANUAL_MENU_GAP, rect.top - MANUAL_MENU_GAP - menuHeight),
      left: rect.left,
    })
  }, [manualMenu])

  useEffect(() => {
    if (!manualMenu) return
    const handleClickOutside = (e) => {
      if (manualBtnRef.current && manualBtnRef.current.contains(e.target)) return
      if (manualMenuRef.current && manualMenuRef.current.contains(e.target)) return
      setManualMenu(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [manualMenu])

  const navigate = useNavigate()
  const authToken = getTutorAuthToken()

  const pdfManuals = [
    {
      name: "Tutor User Manual",
      url: "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/manuals/Tutor_User_Manual_V2.pdf",
    },
    {
      name: "Enrichment Program Manual",
      url: "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/manuals/Enrichment_Program_Manual_V1.pdf",
    },
    {
      name: "Tutor Online Session Manual",
      url: "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/manuals/Tutor Online Session User Manual V1-1.pdf",
    },
    {
      name: "Baseline Math Diagnostics Manual",
      url: "https://s3.ap-southeast-1.amazonaws.com/assets-dev.mygooroo.io/manuals/Baseline Math Diagnostics Tutor Manual V1.0 - 07 Sep 2026.pdf",
    },
  ]

  let tutorKey = null

  if (authToken) {
    try {
      const decoded = jwt_decode(authToken)
      tutorKey = decoded.pk_tut_key || decoded.tut_key || null
    } catch (e) {
      tutorKey = null
    }
  }

  useEffect(() => {
    const fetchReportData = () => {
      if (!authToken) {
        return
      }

      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`

      axios
        .get(BASE_URL + "notification/notificationDetails")
        .then(response => {
          const payload = response?.data

          if (Array.isArray(payload)) {
            setReportCount(payload.length)
          } else {
            setReportCount(Number(payload || 0))
          }
        })
        .catch(error => {
          if (
            error?.response?.status === 403 ||
            error?.response?.status === 401
          ) {
            toast.error(
              "Your authentication has expired. Please log in again.",
              {
                autoClose: 3000,
                onClose: () => {
                  localStorage.clear()
                  navigate("/login")
                },
              }
            )
          }
        })
    }

    const fetchTutorNotifData = () => {
      if (!authToken || !tutorKey) {
        setTutorNotifCount(0)
        return
      }

      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`

      axios
        .get(`${BASE_URL}tutorNotification/notifications?tutorKey=${tutorKey}`)
        .then(response => {
          const notifs = response?.data?.notifications || []
          setTutorNotifCount(
            notifs.filter(item => Number(item?.is_read) === 0).length
          )
        })
        .catch(() => {
          setTutorNotifCount(0)
        })
    }

    fetchReportData()
    fetchTutorNotifData()

    const intervalId = setInterval(() => {
      fetchReportData()
      fetchTutorNotifData()
    }, 20000)

    return () => clearInterval(intervalId)
  }, [authToken, tutorKey, navigate])

  const renderBadge = count => {
    if (!count || Number(count) <= 0) {
      return null
    }

    return (
      <span
        style={{
          position: "absolute",
          top: compact ? "-2px" : "-3px",
          right: compact ? "-3px" : "-4px",
          minWidth: compact ? "13px" : "14px",
          height: compact ? "13px" : "14px",
          borderRadius: "999px",
          background: "#ec4561",
          color: "#fff",
          fontSize: compact ? "7px" : "8px",
          fontWeight: 700,
          lineHeight: compact ? "13px" : "14px",
          textAlign: "center",
          padding: "0 3px",
        }}
      >
        {count > 99 ? "99+" : count}
      </span>
    )
  }

  const baseIconButtonStyle = {
    position: "relative",
    border: "none",
    background: "transparent",
    boxShadow: "none",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  }

  const headerButtonStyle = {
    ...baseIconButtonStyle,
    width: "48px",
    height: "48px",
  }

  const sidebarButtonStyle = {
    ...baseIconButtonStyle,
    width: compact ? "26px" : "28px",
    height: compact ? "26px" : "28px",
    color: "#111",
  }

  if (variant === "sidebar") {
    return (
      <div
        style={{
          background: "#ffffff",
          borderRadius: "2px",
          padding: compact ? "4px 5px" : "5px 6px",
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          alignItems: "center",
          gap: compact ? "2px" : "4px",
        }}
      >
        <div className="d-flex justify-content-center" style={{ position: "relative" }}>
          <button
            type="button"
            ref={manualBtnRef}
            style={sidebarButtonStyle}
            onClick={() => setManualMenu(prev => !prev)}
          >
            <i
              className="fas fa-book-open"
              style={{ fontSize: compact ? "16px" : "17px", color: "#000" }}
            ></i>
          </button>
          {manualMenu && ReactDOM.createPortal(
            <div
              ref={manualMenuRef}
              style={{
                position: "fixed",
                top: manualMenuPos.top,
                left: manualMenuPos.left,
                zIndex: 1060,
                minWidth: "220px",
                background: "#fff",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                padding: "4px 0",
                // Flipping the menu above the button only buys as much room as
                // there is above it. Past that the list has to scroll, or the
                // next manual added starts hiding entries again.
                maxHeight: `calc(100vh - ${MANUAL_MENU_GAP * 2}px)`,
                overflowY: "auto",
              }}
            >
              {pdfManuals.map((manual, idx) => (
                <a
                  key={idx}
                  href={manual.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="dropdown-item"
                  style={{ cursor: "pointer" }}
                  onClick={() => setManualMenu(false)}
                >
                  {manual.name}
                </a>
              ))}
            </div>,
            document.body
          )}
        </div>

        <div className="d-flex justify-content-center">
          <button
            type="button"
            style={sidebarButtonStyle}
            onClick={() => navigate("/AllNotification")}
          >
            <i
              className="fas fa-flag"
              style={{ fontSize: compact ? "16px" : "17px", color: "#000" }}
            ></i>
            {renderBadge(reportCount)}
          </button>
        </div>

        <div className="d-flex justify-content-center">
          <button
            type="button"
            style={sidebarButtonStyle}
            onClick={() => navigate("/TutorNotification")}
          >
            <i
              className="fas fa-bell"
              style={{ fontSize: compact ? "16px" : "17px", color: "#000" }}
            ></i>
            {renderBadge(tutorNotifCount)}
          </button>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <Dropdown
        isOpen={manualMenu}
        toggle={() => setManualMenu(!manualMenu)}
        className="dropdown d-inline-block"
        tag="li"
        onMouseEnter={() => setManualMenu(true)}
        onMouseLeave={() => setManualMenu(false)}
      >
        <DropdownToggle
          className="btn header-item noti-icon waves-effect"
          tag="button"
          id="page-header-manuals-dropdown"
        >
          <i className="fas fa-book-open"></i>
        </DropdownToggle>

        <DropdownMenu end style={{ minWidth: "220px" }}>
          {pdfManuals.map((manual, idx) => (
            <a
              key={idx}
              href={manual.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="dropdown-item"
              style={{ cursor: "pointer" }}
            >
              {manual.name}
            </a>
          ))}
        </DropdownMenu>
      </Dropdown>

      <div className="dropdown d-inline-block">
        <button
          type="button"
          className="btn header-item noti-icon waves-effect"
          onClick={() => navigate("/AllNotification")}
          style={headerButtonStyle}
        >
          <i className="fas fa-flag"></i>
          {renderBadge(reportCount)}
        </button>
      </div>

      <div className="dropdown d-inline-block">
        <button
          type="button"
          className="btn header-item noti-icon waves-effect"
          onClick={() => navigate("/TutorNotification")}
          style={headerButtonStyle}
        >
          <i className="fas fa-bell"></i>
          {renderBadge(tutorNotifCount)}
        </button>
      </div>
    </React.Fragment>
  )
}

NotificationDropdown.propTypes = {
  t: PropTypes.any,
  variant: PropTypes.string,
  compact: PropTypes.bool,
}

export default withTranslation()(NotificationDropdown)
